import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Loader2, Trash2, Lock } from 'lucide-react';
import { trainingAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useTeam } from '../context/TeamContext';
import PremiumGateOverlay from './PremiumGateOverlay';

const EditClipModal = ({ session, video, isOpen, onClose, onSuccess }) => {
    const { toast } = useToast();
    const { plan } = useTeam();
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [formData, setFormData] = useState({
        title: video?.title || '',
        down: video?.play_data?.down?.toString() || '',
        distance: video?.play_data?.distance || '',
        formation: video?.play_data?.formation || '',
        personnel: video?.play_data?.personnel || '',
        play_type: video?.play_data?.play_type || '',
        result: video?.play_data?.result || '',
        gain: video?.play_data?.gain || '',
        kickoff_depth: video?.play_data?.kickoff_depth || '',
        return_yards: video?.play_data?.return_yards || '',
        punt_range: video?.play_data?.punt_range || ''
    });

    useEffect(() => {
        if (isOpen && video) {
            setFormData({
                title: video.title || '',
                down: video.play_data?.down?.toString() || '',
                distance: video.play_data?.distance || '',
                formation: video.play_data?.formation || '',
                personnel: video.play_data?.personnel || '',
                play_type: video.play_data?.play_type || '',
                result: video.play_data?.result || '',
                gain: video.play_data?.gain || '',
                kickoff_depth: video.play_data?.kickoff_depth || '',
                return_yards: video.play_data?.return_yards || '',
                punt_range: video.play_data?.punt_range || ''
            });
        }
    }, [isOpen, video]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const updateData = {
                title: formData.title,
                down: formData.down ? parseInt(formData.down) : null,
                distance: formData.distance ? parseInt(formData.distance) : null,
                formation: formData.formation || null,
                personnel: formData.personnel || null,
                play_type: formData.play_type || null,
                result: formData.result || null,
                gain: formData.gain ? parseInt(formData.gain) : null,
                kickoff_depth: formData.kickoff_depth ? parseInt(formData.kickoff_depth) : null,
                return_yards: formData.return_yards ? parseInt(formData.return_yards) : null,
                punt_range: formData.punt_range ? parseInt(formData.punt_range) : null
            };

            await trainingAPI.updateVideo(session.id, video.id, updateData);

            toast({ title: 'Clip updated successfully' });
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to update clip:', error);
            toast({
                title: 'Update failed',
                description: error.response?.data?.detail || 'An error occurred',
                variant: 'destructive'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this clip? This cannot be undone.')) return;

        setDeleting(true);
        try {
            await trainingAPI.deleteVideo(session.id, video.id);
            toast({ title: 'Clip deleted' });
            onSuccess(); // Triggers refresh in parent
            onClose();
        } catch (error) {
            console.error('Failed to delete clip:', error);
            toast({
                title: 'Delete failed',
                description: 'Could not delete video clip.',
                variant: 'destructive'
            });
        } finally {
            setDeleting(false);
        }
    };

    const isFreePlan = plan !== 'premium';

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={`${isFreePlan ? 'p-0 overflow-hidden bg-[#0a0a0b] border-gray-800 sm:max-w-[800px]' : 'w-full max-w-md mx-auto sm:max-w-[500px] p-0 overflow-hidden bg-[#121214] border-t border-gray-800 sm:border rounded-t-[2rem] sm:rounded-xl mt-auto mb-0 sm:my-auto max-h-[90vh] sm:max-h-[85vh] flex flex-col'} text-white`}>
                {!isFreePlan && (
                    <div className="bg-gradient-to-r from-blue-900/40 via-indigo-900/30 to-[#121214] border-b border-gray-800/60 p-5 sm:p-6 sticky top-0 z-20 backdrop-blur-xl shrink-0">
                        <div className="w-12 h-1.5 bg-gray-800 rounded-full mx-auto mb-4 sm:hidden" />
                        <DialogHeader className="flex flex-row items-center justify-between space-y-0">
                            <DialogTitle className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-300">
                                Edit Clip Details
                            </DialogTitle>
                        </DialogHeader>
                    </div>
                )}

                {isFreePlan ? (
                    <div className="relative w-full h-[750px] flex flex-col">
                        <PremiumGateOverlay onClose={onClose} />
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-6 relative min-h-[400px] custom-scrollbar">
                        <div className="space-y-5">
                            <div className="space-y-2.5">
                                <Label htmlFor="title" className="text-gray-300 font-medium text-sm ml-1">Title</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="bg-white/5 border-white/10 text-white rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 h-11"
                                />
                            </div>
                            {/* ... Rest of form ... */}

                            {/* ... Rest of form content ... */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2.5">
                                    <Label htmlFor="down" className="text-gray-300 font-medium text-sm ml-1">Down</Label>
                                    <Select
                                        value={formData.down}
                                        onValueChange={(v) => setFormData({ ...formData, down: v })}
                                    >
                                        <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:ring-blue-500/50">
                                            <SelectValue placeholder="Select down" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#1a1a1b] border-gray-800 text-white rounded-xl">
                                            <SelectItem value="1">1st Down</SelectItem>
                                            <SelectItem value="2">2nd Down</SelectItem>
                                            <SelectItem value="3">3rd Down</SelectItem>
                                            <SelectItem value="4">4th Down</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2.5">
                                    <Label htmlFor="distance" className="text-gray-300 font-medium text-sm ml-1">Distance</Label>
                                    <Input
                                        id="distance"
                                        type="number"
                                        value={formData.distance}
                                        onChange={(e) => setFormData({ ...formData, distance: e.target.value })}
                                        placeholder="Yards"
                                        className="bg-white/5 border-white/10 text-white rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 h-11"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2.5">
                                    <Label htmlFor="formation" className="text-gray-300 font-medium text-sm ml-1">Formation</Label>
                                    <Input
                                        id="formation"
                                        value={formData.formation}
                                        onChange={(e) => setFormData({ ...formData, formation: e.target.value })}
                                        className="bg-white/5 border-white/10 text-white rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 h-11"
                                    />
                                </div>

                                <div className="space-y-2.5">
                                    <Label htmlFor="personnel" className="text-gray-300 font-medium text-sm ml-1">Personnel</Label>
                                    <Input
                                        id="personnel"
                                        value={formData.personnel}
                                        onChange={(e) => setFormData({ ...formData, personnel: e.target.value })}
                                        className="bg-white/5 border-white/10 text-white rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 h-11"
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <Label className="text-gray-300 font-medium text-sm ml-1 block">Play Type</Label>
                                <div className="grid grid-cols-3 gap-2 sm:gap-3 bg-white/5 p-1 rounded-2xl border border-white/5">
                                    {['Pass', 'Run', 'Special'].map((type) => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, play_type: type, result: '', gain: '', kickoff_depth: '', return_yards: '', punt_range: '' })}
                                            className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${formData.play_type === type
                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40'
                                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                                }`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Conditional Result Logic */}
                            {['Pass', 'Run'].includes(formData.play_type) && (
                                <div className="space-y-2.5">
                                    <Label htmlFor="result" className="text-gray-300 font-medium text-sm ml-1">Result</Label>
                                    <Select
                                        value={formData.result}
                                        onValueChange={(v) => setFormData({ ...formData, result: v })}
                                    >
                                        <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:ring-blue-500/50">
                                            <SelectValue placeholder="Select result" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#1a1a1b] border-gray-800 text-white rounded-xl">
                                            <SelectItem value="Touchdown">Touchdown</SelectItem>
                                            <SelectItem value="First Down">First Down</SelectItem>
                                            <SelectItem value="Gain">Gain</SelectItem>
                                            {formData.play_type === 'Pass' && <SelectItem value="Incomplete">Incomplete</SelectItem>}
                                            <SelectItem value="Turnover">Turnover</SelectItem>
                                            <SelectItem value="Sack">Sack</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* Gain Input for standard plays */}
                            {['Pass', 'Run'].includes(formData.play_type) && ['First Down', 'Gain'].includes(formData.result) && (
                                <div className="space-y-2.5">
                                    <Label htmlFor="gain" className="text-gray-300 font-medium text-sm ml-1">Yards Gained</Label>
                                    <Input
                                        id="gain"
                                        type="number"
                                        value={formData.gain}
                                        onChange={(e) => setFormData({ ...formData, gain: e.target.value })}
                                        placeholder="Yards"
                                        className="bg-white/5 border-white/10 text-white rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 h-11"
                                    />
                                </div>
                            )}

                            {/* Special Inputs */}
                            {formData.play_type === 'Special' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2.5">
                                        <Label htmlFor="kickoff_depth" className="text-gray-300 font-medium text-sm ml-1">Kick/Punt Range</Label>
                                        <Input
                                            id="kickoff_depth"
                                            type="number"
                                            value={formData.kickoff_depth || formData.punt_range || ''}
                                            onChange={(e) => setFormData({ ...formData, kickoff_depth: e.target.value, punt_range: e.target.value })}
                                            placeholder="Yards"
                                            className="bg-white/5 border-white/10 text-white rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 h-11"
                                        />
                                    </div>
                                    <div className="space-y-2.5">
                                        <Label htmlFor="return_yards" className="text-gray-300 font-medium text-sm ml-1">Return Yards</Label>
                                        <Input
                                            id="return_yards"
                                            type="number"
                                            value={formData.return_yards}
                                            onChange={(e) => setFormData({ ...formData, return_yards: e.target.value })}
                                            placeholder="Yards"
                                            className="bg-white/5 border-white/10 text-white rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 h-11"
                                        />
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                )}

                {plan === 'premium' && (
                    <div className="bg-[#121214]/90 backdrop-blur-md border-t border-gray-800/60 p-5 sm:p-6 sticky bottom-0 z-20 shrink-0">
                        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between items-center">
                            <Button
                                type="button"
                                variant="destructive"
                                onClick={handleDelete}
                                disabled={deleting || saving}
                                className="w-full sm:w-auto bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 rounded-xl h-12 sm:h-11"
                            >
                                {deleting ? (
                                    <Loader2 className="w-5 h-5 sm:w-4 sm:h-4 animate-spin" />
                                ) : (
                                    <>
                                        <Trash2 className="w-5 h-5 sm:w-4 sm:h-4 mr-2" />
                                        Delete Clip
                                    </>
                                )}
                            </Button>

                            <div className="flex gap-3 w-full sm:w-auto mt-2 sm:mt-0">
                                <Button
                                    variant="ghost"
                                    onClick={onClose}
                                    className="flex-1 sm:flex-none text-gray-300 hover:text-white hover:bg-white/5 rounded-xl h-12 sm:h-11 font-medium"
                                    disabled={saving || deleting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSave}
                                    disabled={saving || deleting}
                                    className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-500 text-white rounded-xl h-12 sm:h-11 font-medium shadow-lg shadow-blue-900/20"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-5 h-5 sm:w-4 sm:h-4 mr-2 animate-spin" />
                                            Saving
                                        </>
                                    ) : 'Save Changes'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default EditClipModal;
